/**
 * 智能客服 - 机器人 接口定义
 */
import { DEFAULT_PAGE_SIZE, LARGE_PAGE_SIZE } from '@/constants/api';
import { PUBLISH_STATUS } from '@/constants/robots';
import { apiGet, apiPost, apiPut, apiRequest } from '@/utils/request';
import { parseListResponse, parsePaginatedResponse } from '@/utils/apiResponse';
import type { RobotAgentGroup } from '@/types/robots';
import type {
  AiTestBatchPreviewMessage,
  AiTestCreateSessionPayload,
  AiTestCreateSessionResp,
  AiTestSessionDetail,
  AiTestSessionMessagesResp,
} from '@/types/aiTest';

export async function queryRobots(params: Record<string, unknown> = {}) {
  const d = await apiGet('/robots', { per_page: LARGE_PAGE_SIZE, ...params });
  return parseListResponse(d);
}

/**
 * 按 Agent 类型分组的机器人列表，供「先选类型再选机器人」的二级下拉使用。
 *
 * group_by=agent_type 下后端返回全量、忽略分页，list 每项是一个类型分组。
 */
export async function queryRobotsGrouped(params: Record<string, unknown> = {}) {
  const d = await apiGet('/robots', { ...params, group_by: 'agent_type' });
  return parseListResponse<RobotAgentGroup>(d);
}

export async function queryKnowledgeBases(robotId?: number) {
  const d = await apiGet('/ai-test/knowledge-bases', robotId ? { robot_id: robotId } : {});
  return parseListResponse(d);
}

const _robotShowCache: Record<string, Promise<unknown>> = {};

export const robotsApi = {
  list: (params: Record<string, unknown> = {}) =>
    apiGet('/robots', { per_page: LARGE_PAGE_SIZE, ...params }).then((d) =>
      parsePaginatedResponse(d),
    ),
  show: (id: number, versionType = 0) => {
    const key = `${id}:${versionType}:${localStorage.getItem('token') ?? ''}`;
    if (!_robotShowCache[key]) {
      _robotShowCache[key] = apiGet(`/robots/${id}`, { version_type: versionType }).finally(() => {
        delete _robotShowCache[key];
      });
    }
    return _robotShowCache[key];
  },
  invalidateShowCache: (id: number) => {
    Object.keys(_robotShowCache).forEach((k) => {
      if (k.startsWith(`${id}:`)) delete _robotShowCache[k];
    });
  },
  templates: () => apiGet('/robot-templates').then((d) => parseListResponse(d)),
  create: (data: Record<string, unknown>) => apiPost('/robots', data),
  update: (id: number, data: Record<string, unknown>) => apiPut(`/robots/${id}`, data),
  rename: (id: number, name: string) => apiPut(`/robots/${id}/rename`, { name }),
  copy: (id: number) => apiPost(`/robots/${id}/copy`, {}),
  delete: (id: number) => apiRequest(`/robots/${id}`, { method: 'DELETE' }),
  /**
   * 发布：传 publish_status=1，覆盖线上配置。
   *
   * timeout: 0 不限制耗时：导购Agent 发布时后端会联动重算商品摘要并入向量库，
   * 耗时随商品数量浮动，走默认的 VITE_TIMEOUT 会在后端还在处理时被前端提前中断，
   * 用户以为发布失败反复点击。
   */
  publish: (id: number) =>
    apiRequest(`/robots/${id}`, {
      method: 'PUT',
      data: { publish_status: PUBLISH_STATUS.DRAFT },
      timeout: 0,
    }),
  online: (id: number) => apiPut(`/robots/${id}`, { publish_status: PUBLISH_STATUS.RUNNING }),
  offline: (id: number) => apiPut(`/robots/${id}`, { publish_status: PUBLISH_STATUS.OFFLINE }),
  storeFlow: (id: number, data: Record<string, unknown>) => apiPost(`/robots/${id}/flows`, data),
  deleteFlow: (id: number, flowId: number) =>
    apiRequest(`/robots/${id}/flows/${flowId}`, { method: 'DELETE' }),
  updateFlow: (id: number, data: Record<string, unknown>) => apiPut(`/robots/${id}/flow`, data),
  qaCategories: (id: number) =>
    apiGet(`/robots/${id}/qa-categories`).then((d) => parseListResponse(d)),
  qaStoreCategory: (id: number, data: Record<string, unknown>) =>
    apiPost(`/robots/${id}/qa-categories`, data),
  qaUpdateCategory: (id: number, catId: number, data: Record<string, unknown>) =>
    apiPut(`/robots/${id}/qa-categories/${catId}`, data),
  qaDeleteCategory: (id: number, catId: number) =>
    apiRequest(`/robots/${id}/qa-categories/${catId}`, { method: 'DELETE' }),
  qaTemplates: (id: number, params?: Record<string, unknown>) =>
    apiGet(`/robots/${id}/qa-templates`, params).then((d) => parseListResponse(d)),
  qaTemplatesPaged: (id: number, params?: Record<string, unknown>) =>
    apiGet(`/robots/${id}/qa-templates`, { per_page: DEFAULT_PAGE_SIZE, ...params }).then((d) =>
      parsePaginatedResponse(d),
    ),
  qaImportTemplates: (id: number, data: FormData) =>
    apiRequest(`/robots/${id}/qa-templates/import`, { method: 'POST', data }),
  qaStoreTemplate: (id: number, data: Record<string, unknown>) =>
    apiPost(`/robots/${id}/qa-templates`, data),
  qaUpdateTemplate: (id: number, tid: number, data: Record<string, unknown>) =>
    apiPut(`/robots/${id}/qa-templates/${tid}`, data),
  qaDeleteTemplate: (id: number, tid: number) =>
    apiRequest(`/robots/${id}/qa-templates/${tid}`, { method: 'DELETE' }),
};

/** AI 测试、质量报告 - 机器人页面使用 */
export const aiTestApi = {
  /** 基于 ai-test-sessions/* 的测试任务接口 */
  sessions: {
    create: (data: AiTestCreateSessionPayload) =>
      apiPost<AiTestCreateSessionResp>('/ai-test-sessions/create', data),
    send: (data: {
      session_id: number;
      content: string;
      msg_type?: number;
      user_id?: number;
      account_id?: number;
    }) => apiPost('/ai-test-sessions/send', data),
    /** 模拟同一用户再次进线（仅 type=1）：插入「用户模拟再次进线」系统消息并重新拉取本轮欢迎语 */
    reenter: (session_id: number) =>
      apiPost<{ session_id: number }>('/ai-test-sessions/reenter', { session_id }),
    startBatch: (session_id: number, conversations?: AiTestBatchPreviewMessage[][]) =>
      apiPost<{ session_id: number; user_ids: number[] }>('/ai-test-sessions/start-batch-test', {
        session_id,
        conversations,
      }),
    startUser: (session_id: number) => apiPost('/ai-test-sessions/start-user-test', { session_id }),
    stop: (session_id: number) => apiPost('/ai-test-sessions/stop', { session_id }),
    show: (params: { session_id?: number; robot_id?: number; type?: number }) =>
      apiGet<AiTestSessionDetail>('/ai-test-sessions/show', params),
    status: (session_id: number) =>
      apiGet<{ session_id: number; type: number; status: number }>('/ai-test-sessions/status', {
        session_id,
      }),
    messages: (session_id: number, user_id: number) =>
      apiGet<AiTestSessionMessagesResp>('/ai-test-sessions/messages', { session_id, user_id }),
  },
};

export const qualityReportApi = {
  get: (robotId: number) => apiGet(`/quality-report/${robotId}`),
};

/** 评论回复 - 表格化管理，每个机器人下多条 */
export const commentRepliesApi = {
  list: (robotId: number, params?: Record<string, unknown>) =>
    apiGet(`/robots/${robotId}/comment-replies`, params).then((d) => parsePaginatedResponse(d)),
  create: (robotId: number, data: Record<string, unknown>) =>
    apiPost(`/robots/${robotId}/comment-replies`, data),
  update: (robotId: number, id: number, data: Record<string, unknown>) =>
    apiPut(`/robots/${robotId}/comment-replies/${id}`, data),
  delete: (robotId: number, id: number) =>
    apiRequest(`/robots/${robotId}/comment-replies/${id}`, { method: 'DELETE' }),
};

/** 唤醒语 - 表格化管理，每个机器人下多条 */
export const wakeUpWordsApi = {
  list: (robotId: number, params?: Record<string, unknown>) =>
    apiGet(`/robots/${robotId}/wake-up-words`, params).then((d) => parsePaginatedResponse(d)),
  create: (robotId: number, data: Record<string, unknown>) =>
    apiPost(`/robots/${robotId}/wake-up-words`, data),
  update: (robotId: number, id: number, data: Record<string, unknown>) =>
    apiPut(`/robots/${robotId}/wake-up-words/${id}`, data),
  delete: (robotId: number, id: number) =>
    apiRequest(`/robots/${robotId}/wake-up-words/${id}`, { method: 'DELETE' }),
};

/** 问答库分组 */
export const robotCategoryGroupsApi = {
  list: (params: Record<string, unknown> = {}) =>
    apiGet('/robot-category-groups', params).then((d) => parseListResponse(d)),
  create: (data: Record<string, unknown>) => apiPost('/robot-category-groups', data),
  update: (id: number, data: Record<string, unknown>) =>
    apiPut(`/robot-category-groups/${id}`, data),
  delete: (id: number) => apiRequest(`/robot-category-groups/${id}`, { method: 'DELETE' }),
};

/** 问答列表 */
export const robotCategoryRulesApi = {
  list: (params: Record<string, unknown> = {}) =>
    apiGet('/robot-category-rules', { per_page: DEFAULT_PAGE_SIZE, ...params }).then((d) =>
      parsePaginatedResponse(d),
    ),
  detail: (id: number, versionType = 0) =>
    apiGet(`/robot-category-rules/${id}`, { version_type: versionType }),
  create: (data: Record<string, unknown>) => apiPost('/robot-category-rules', data),
  update: (id: number, data: Record<string, unknown>) =>
    apiPut(`/robot-category-rules/${id}`, data),
  delete: (id: number) => apiRequest(`/robot-category-rules/${id}`, { method: 'DELETE' }),
  copy: (id: number) => apiPost(`/robot-category-rules/${id}/copy`, {}),
  downloadTemplate: () => apiGet('/robot-category-rules/download-template'),
  import: (data: FormData) => apiRequest('/robot-category-rules/import', { method: 'POST', data }),
};

/** 内置意图 */
export const robotIntentRulesApi = {
  list: (params: Record<string, unknown> = {}) =>
    apiGet('/robot-intent-rules', { per_page: DEFAULT_PAGE_SIZE, ...params }).then((d) =>
      parsePaginatedResponse(d),
    ),
  detail: (id: number, versionType = 0) =>
    apiGet(`/robot-intent-rules/${id}`, { version_type: versionType }),
  update: (id: number, data: Record<string, unknown>) => apiPut(`/robot-intent-rules/${id}`, data),
};

/** 开口引导语 & 唤醒语 - 统一消息组管理（/robot-message-groups） */
export const robotMessageGroupsApi = {
  list: (params: Record<string, unknown> = {}) =>
    apiGet('/robot-message-groups', { per_page: DEFAULT_PAGE_SIZE, ...params }).then((d) =>
      parsePaginatedResponse(d),
    ),
  detail: (id: number, versionType = 0) =>
    apiGet(`/robot-message-groups/${id}`, { version_type: versionType }),
  create: (data: Record<string, unknown>) => apiPost('/robot-message-groups', data),
  update: (id: number, data: Record<string, unknown>) =>
    apiPut(`/robot-message-groups/${id}`, data),
  delete: (id: number) => apiRequest(`/robot-message-groups/${id}`, { method: 'DELETE' }),
};

/** 信息采集配置 */
export const robotInfoCollectApi = {
  list: (params: Record<string, unknown> = {}) =>
    apiGet('/robot-info-collect/index', { per_page: DEFAULT_PAGE_SIZE, ...params }).then((d) =>
      parsePaginatedResponse(d),
    ),
  update: (data: {
    id?: number | null;
    conf_id?: number | null;
    robot_id?: number | null;
    enabled: number;
    version_type?: number | null;
  }) => {
    const payload: Record<string, unknown> = { enabled: data.enabled };
    if (data.id != null) payload.id = data.id;
    if (data.conf_id != null) payload.conf_id = data.conf_id;
    if (data.robot_id != null) payload.robot_id = data.robot_id;
    if (data.version_type != null) payload.version_type = data.version_type;
    return apiPut('/robot-info-collect/update', payload);
  },
};

/** 开口引导语 - 表格化管理，每个机器人下多条 */
export const openingGreetingsApi = {
  list: (robotId: number, params?: Record<string, unknown>) =>
    apiGet(`/robots/${robotId}/opening-greetings`, params).then((d) => parsePaginatedResponse(d)),
  create: (robotId: number, data: Record<string, unknown>) =>
    apiPost(`/robots/${robotId}/opening-greetings`, data),
  update: (robotId: number, id: number, data: Record<string, unknown>) =>
    apiPut(`/robots/${robotId}/opening-greetings/${id}`, data),
  delete: (robotId: number, id: number) =>
    apiRequest(`/robots/${robotId}/opening-greetings/${id}`, { method: 'DELETE' }),
};
